import React, { useMemo, useEffect, useState } from "react";
import { Hooks, Util } from "@xt/xtbase";
import { BackHeader } from "@xt/Nav";
import { get_ticker24h } from "@/api/v4/market";
import { useStore } from "@/models";
import { AI_SIGNAL_DEFAULT, AI_SYMBOLS } from "../aiSignals";
import { formatCompactNumber, translateSignalLabel } from "../aiSelectFormat";
import styles from "./index.module.less";

const { useTranslation, useIntl } = Hooks;
const { Big, showRate, getUrl } = Util;

const TABLE_PAGE_SIZE = 5;

function getPricePrecision(symbol, marketConfig) {
  return marketConfig?.[symbol]?.buyPoint ?? 2;
}

function getCoinIcon(symbol, fund) {
  if (!symbol) return require("@/assets/images/common/icon404.png").default;
  const [base] = symbol.split("_");
  const rawLogo = fund?.currencyMap?.[base]?.logo || fund.getCurrencyIcon(base);
  if (!rawLogo) return require("@/assets/images/common/icon404.png").default;
  if (typeof window === "undefined") return rawLogo;
  const domain = window.document.domain;
  const domain2 = domain.includes(".") ? domain.substring(domain.indexOf(".") + 1) : domain;
  return !domain.startsWith("xt") ? rawLogo.replace("xt.com", domain2) : rawLogo;
}

function formatName(symbol, fund) {
  if (!symbol) return "";
  const [base] = symbol.split("_");
  return fund.getCurrencyDisplayName((base || "").toLowerCase());
}

function getSignalDirection(signalText) {
  const text = String(signalText || "").toLowerCase();
  if (text.includes("strong positive")) return "strong-positive";
  if (text.includes("positive")) return "positive";
  if (text.includes("neutral")) return "neutral";
  if (text.includes("strong negative")) return "strong-negative";
  if (text.includes("negative")) return "negative";
  return "positive";
}

const AiSelectHome = () => {
  const { fund, market } = useStore();
  const t = useTranslation();
  const intl = useIntl();
  const [loading, setLoading] = useState(true);
  const [tickerMap, setTickerMap] = useState({});
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");
  const [showDisclaimerSheet, setShowDisclaimerSheet] = useState(false);
  const [tablePage, setTablePage] = useState(1);

  const displaySignals = useMemo(() => AI_SYMBOLS.slice(0, 3), []);
  const tableTotalPages = Math.max(1, Math.ceil(AI_SYMBOLS.length / TABLE_PAGE_SIZE));
  const tableSlice = useMemo(() => {
    const start = (tablePage - 1) * TABLE_PAGE_SIZE;
    return AI_SYMBOLS.slice(start, start + TABLE_PAGE_SIZE);
  }, [tablePage]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    get_ticker24h({
      params: { symbols: displaySignals.map((item) => item.symbol).join(",") },
    })
      .then((tickerData) => {
        if (cancelled) return;
        const tickerList = Array.isArray(tickerData) ? tickerData : [];
        const map = {};
        tickerList.forEach((item) => {
          map[item.s] = item;
        });
        setTickerMap(map);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [displaySignals]);

  useEffect(() => {
    setLastUpdatedAt(new Date().toISOString().replace("T", " ").slice(0, 19) + " (UTC)");
  }, []);

  return (
    <div className={styles.page}>
      <BackHeader onBack={() => window.history.back()} />
      <div className={styles.content}>
        <div className={styles.intro}>
          <h1>{t("aiselect.title")}</h1>
          <p className={styles.disclaimer}>{t("aiselect.disclaimer")}</p>
          <button type="button" className={styles.moreBtn} onClick={() => setShowDisclaimerSheet(true)}>
            {t("aiselect.more")}
          </button>
          <p className={styles.updatedAt}>
            {lastUpdatedAt ? t("aiselect.lastUpdated", { time: lastUpdatedAt }) : t("aiselect.lastUpdated", { time: "—" })}
          </p>
        </div>

        <div className={styles.list}>
          {displaySignals.map((item) => {
            const ticker = tickerMap[item.symbol] || {};
            const isUp = +(ticker?.cr || ticker?.r || 0) >= 0;
            const scoreStr = String(item.score || AI_SIGNAL_DEFAULT.score);
            const scoreNum = Number(scoreStr) || 0;
            const scoreTone = scoreNum >= 7 ? styles.positive : scoreNum >= 5 ? styles.middle : styles.negative;
            const signalLabel = translateSignalLabel(item.signal || AI_SIGNAL_DEFAULT.signal, t);
            return (
              <button
                key={item.symbol}
                type="button"
                className={styles.card}
                onClick={() => {
                  window.location.href = getUrl(`/aiselect/detail/${item.symbol}`);
                }}
              >
                <div className={styles.left}>
                  <div className={styles.coinRow}>
                    <img src={getCoinIcon(item.symbol, fund)} alt="" className={styles.icon} />
                    <div className={styles.info}>
                      <div className={styles.name}>{formatName(item.symbol, fund)}</div>
                      <div className={styles.priceRow}>
                        <span className={styles.price}>
                          $
                          {Big(ticker?.c || 0).toFixedCy(getPricePrecision(item.symbol, market.marketConfig))}
                        </span>
                        <span className={`${styles.rate} ${isUp ? styles.up : styles.down}`}>
                          {showRate((ticker?.cr || ticker?.r || 0) * 100)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.tradeNow}>
                    {intl.formatMessage({ id: "aicopy.tradeNow", defaultMessage: "Trade Now" })}
                  </div>
                </div>
                <div className={styles.scoreChart}>
                  <div className={styles.diamondOuter} />
                  <div className={styles.diamondMid} />
                  <div className={`${styles.diamondInner} ${scoreTone}`} />
                  <div className={styles.scoreText}>
                    <strong>{scoreStr}</strong>
                    <span>{signalLabel}</span>
                  </div>
                </div>
              </button>
            );
          })}
          {loading ? <div className={styles.status}>{t("aicopy.loading")}</div> : null}
        </div>

        <div className={styles.tableWrap}>
          <div className={styles.tableHeader}>
            <div className={styles.headName}>
              {t("aiselect.tableName")} <span className={styles.sortArrow}>▲</span>
            </div>
            <div className={styles.headSignal}>
              {t("aiselect.tableAiRating")} <span className={styles.sortArrow}>▲</span>
            </div>
            <div className={styles.headVol}>
              {t("aiselect.tableSocialVol24h")} <span className={styles.sortArrow}>▲</span>
            </div>
          </div>

          <div className={styles.tableBody}>
            {tableSlice.map((item, index) => {
              const rawSignal = item.signal || AI_SIGNAL_DEFAULT.signal;
              const signalLabel = translateSignalLabel(rawSignal, t);
              const direction = getSignalDirection(rawSignal);
              const signalClass =
                direction === "strong-negative" || direction === "negative"
                  ? styles.signalDown
                  : direction === "neutral"
                    ? styles.signalNeutral
                    : styles.signalUp;
              const iconClass =
                direction === "strong-negative"
                  ? styles.iconDoubleDown
                  : direction === "negative"
                    ? styles.iconDown
                    : direction === "neutral"
                      ? styles.iconNeutral
                      : direction === "strong-positive"
                        ? styles.iconDoubleUp
                        : styles.iconUp;

              const volStr = formatCompactNumber(153070);
              const volSub = t("aiselect.bullishLabel");

              return (
                <button
                  key={`ai-list-row-${item.symbol}-${index}`}
                  type="button"
                  className={styles.tableRow}
                  onClick={() => {
                    window.location.href = getUrl(`/aiselect/detail/${item.symbol}`);
                  }}
                >
                  <div className={styles.rowName}>
                    <img src={getCoinIcon(item.symbol, fund)} alt="" className={styles.rowIcon} />
                    <span>{formatName(item.symbol, fund)}</span>
                  </div>

                  <div className={`${styles.rowSignal} ${signalClass}`}>
                    <i className={iconClass} />
                    <span>{signalLabel}</span>
                  </div>

                  <div className={styles.rowVol}>
                    <strong>{volStr}</strong>
                    <span className={styles.volSub}>{volSub}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageArrow}
            disabled={tablePage <= 1}
            onClick={() => setTablePage((p) => Math.max(1, p - 1))}
          >
            <i className={styles.leftArrow} />
          </button>
          {Array.from({ length: tableTotalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={`page-${p}`}
              type="button"
              className={`${styles.pageCell} ${p === tablePage ? styles.pageCellActive : ""}`}
              onClick={() => setTablePage(p)}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            className={styles.pageArrow}
            disabled={tablePage >= tableTotalPages}
            onClick={() => setTablePage((p) => Math.min(tableTotalPages, p + 1))}
          >
            <i className={styles.rightArrow} />
          </button>
        </div>
      </div>

      {showDisclaimerSheet ? (
        <div className={styles.infoSheetMask} onClick={() => setShowDisclaimerSheet(false)}>
          <div className={styles.infoSheet} onClick={(e) => e.stopPropagation()}>
            <div className={styles.infoSheetTopLine} />
            <div className={styles.infoSheetBody}>
              <p className={styles.infoSheetDesc}>{t("aiselect.disclaimer")}</p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AiSelectHome;
